import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { IoSearchOutline } from "react-icons/io5";

import SearchSection from "./searchSection";
import Loading from "../../common/loading";
import { SearchContainer } from "./styled";

export default function SearchForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryKeyword = searchParams.get("keyword") || "";

  const { cmpnyNo, url, theme } = useOutletContext();
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState({ product: {}, portfolio: {}, notice: {} });
  const [inputKeyword, setInputKeyword] = useState(queryKeyword);
  const [displayKeyword, setDisplayKeyword] = useState("");

  useEffect(() => {
    if (queryKeyword && queryKeyword.length >= 2) {
      setInputKeyword(queryKeyword);
      fetchSearchData(queryKeyword);
    }
  }, [location.search]);

  const validate = (value) => {
    if (value.trim().length < 2) {
      toast.error("검색어 두 글자 이상 입력해 주세요.");
      return false;
    }
    return true;
  };

  const handleSearch = () => {
    if (!validate(inputKeyword)) return;
    navigate(`/shop/${url ? `${url}/` : ""}search?keyword=${encodeURIComponent(inputKeyword)}`);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const fetchSearchData = async (keyword) => {
    setIsLoading(true);
    try {
      const [product, portfolio, notice] = await Promise.all([
        fetchProduct(keyword),
        fetchPortfolio(keyword),
        fetchNotice(keyword),
      ]);

      setSearched({
        product: product || { type: "product", name: "상품", count: 0, data: [] },
        portfolio: portfolio || { type: "portfolio", name: "포트폴리오", count: 0, data: [] },
        notice: notice || { type: "notice", name: "공지", count: 0, data: [] },
      });

      setDisplayKeyword(keyword);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProduct = async (keyword) => {
    if (!cmpnyNo) return;

    try {
      const res = await axios.post("/erp/vendorProducts/get", {
        query: `
          query VendorProducts($cmpnyNo: Int!, $name: String) {
            vendorProducts(filter: { cmpnyNo: $cmpnyNo, name: $name }) {
              totalCount
              products {
                id
                name
                brand
                categoryId
                media
                price
              }
            }
          }
        `,
        variables: {
          cmpnyNo: parseInt(cmpnyNo, 10),
          name: keyword.trim(),
        },
      });

      const result = res.data;

      return {
        type: "product",
        name: "상품",
        count: result?.totalCount || 0,
        data: result?.products || [],
      };
    } catch (err) {
      console.error("상품 검색 실패:", err);
    }
  };

  const fetchPortfolio = async (keyword) => {
    try {
      const res = await axios.post("/api/portMgmtList", {
        cmpnyNo,
        useYn: "Y",
        notitleYn: "Y",
        offset: 0,
        pageNumber: 0,
        pageSize: 10,
        paged: true,
        keyword,
      });

      return {
        type: "portfolio",
        name: "포트폴리오",
        count: res.data?.totalElements || 0,
        data: res.data?.content || [],
      };
    } catch (err) {
      console.error("포트폴리오 검색 실패:", err);
    }
  };

  const fetchNotice = async (keyword) => {
    try {
      const res = await axios.post("/api/noticeList", {
        gbn: "N",
        delYn: "N",
        cmpnyNo,
        offset: 0,
        pageNumber: 0,
        pageSize: 10,
        paged: true,
        noticeSj: keyword,
      });

      return {
        type: "notice",
        name: "공지",
        count: res.data?.totalElements || 0,
        data: res.data?.content || [],
      };
    } catch (err) {
      console.error("공지 검색 실패:", err);
    }
  };

  return (
    <SearchContainer className={`theme-${theme}`}>
      <h3>
        <span>통합검색</span>
      </h3>

      {!["0", "2"].includes(String(theme)) && (
        <div className="form">
          <input
            type="text"
            placeholder="검색어를 입력해 주세요."
            value={inputKeyword}
            onChange={(e) => setInputKeyword(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          <button onClick={handleSearch}>
            <IoSearchOutline />
          </button>
        </div>
      )}

      <div className="keyword">
        {!displayKeyword ? (
          <span>검색어를 입력해 주세요.</span>
        ) : (
          <span>
            <strong className="color">'{displayKeyword}'</strong> 검색 결과 총{" "}
            <strong>
              {Object.values(searched).reduce((sum, item) => sum + (item.count ?? 0), 0)}
            </strong>
            건
          </span>
        )}
      </div>

      <div className="contents">
        {isLoading ? (
          <Loading />
        ) : (
          Object.keys(searched).map((key) =>
            searched[key].count > 0 ? (
              <SearchSection
                key={key}
                data={searched[key]}
                title={searched[key].name}
                items={searched[key].data}
                url={url}
              />
            ) : null,
          )
        )}
      </div>
    </SearchContainer>
  );
}
